import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Statistic,
  Table,
  Spin,
  Typography,
  Tag,
  Button,
  Space,
  Input,
  Card,
  Tooltip as AntTooltip
} from "antd";
import {
  CarOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  UserOutlined
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie
} from "recharts";
import axios from "axios";
import BASEURL from "../API/Config";
import * as XLSX from "xlsx";

const { Text, Title } = Typography;
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"];

const DeliveryBoyReports = () => {
  const [loading, setLoading] = useState(true);
  const [performance, setPerformance] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const token = localStorage.getItem("token");

  const fetchData = async () => {
    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };
      const res = await axios.get(`${BASEURL}/reports/delivery-performance`, config);
      setPerformance(res.data || []);
    } catch (error) {
      console.error("Error fetching delivery boy reports:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExport = () => {
    const exportData = performance.map((item, index) => ({
      "S.No": index + 1,
      "Name": item.name || "N/A",
      "Total Assigned": item.totalAssigned || 0,
      "Total Delivered": item.totalDelivered || 0,
      "Success Rate (%)": item.totalAssigned ? ((item.totalDelivered / item.totalAssigned) * 100).toFixed(1) : 0
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Delivery Boy Performance");
    XLSX.writeFile(wb, "Delivery_Boy_Report.xlsx");
  };

  const filteredData = performance.filter(item => 
    String(item.name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Spin size="large" tip="Loading Delivery Reports..." />
      </div>
    );
  }

  const chartData = performance.slice(0, 10).map(item => ({
    name: item.name?.split(" ")[0] || "Boy",
    delivered: item.totalDelivered,
    assigned: item.totalAssigned
  }));

  const overallStats = {
    totalBoys: performance.length,
    totalAssigned: performance.reduce((a, b) => a + b.totalAssigned, 0),
    totalDelivered: performance.reduce((a, b) => a + b.totalDelivered, 0),
  };

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1 }}>Analytics</div>
          <h2 style={{ margin: 0, fontFamily: "Outfit,sans-serif", fontSize: 22, fontWeight: 700, color: "#212121" }}>Delivery Personnel Reports</h2>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>Refresh</Button>
          <Button 
            type="primary" 
            icon={<FileExcelOutlined />} 
            onClick={handleExport}
            style={{ background: "#107c41", borderColor: "#107c41", borderRadius: 4 }}
          >
            Export to Excel
          </Button>
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card">
            <Statistic
              title="Total Delivery Boys"
              value={overallStats.totalBoys}
              prefix={<UserOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card">
            <Statistic
              title="Total Assigned Orders"
              value={overallStats.totalAssigned}
              prefix={<ClockCircleOutlined style={{ color: "#faad14" }} />}
              valueStyle={{ fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card">
            <Statistic
              title="Orders Delivered"
              value={overallStats.totalDelivered}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a", fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} className="report-card">
            <Statistic
              title="Overall Efficiency"
              value={overallStats.totalAssigned ? (overallStats.totalDelivered / overallStats.totalAssigned * 100).toFixed(1) : 0}
              suffix="%"
              prefix={<CarOutlined style={{ color: "#722ed1" }} />}
              valueStyle={{ color: "#722ed1", fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <div style={{ background: "#fff", padding: 24, borderRadius: 12, border: "1px solid #e8ecf0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <Title level={5} style={{ marginBottom: 20 }}>Performance by Personnel (Top 10)</Title>
            <div style={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
                  <Tooltip cursor={{ fill: "rgba(0,0,0,0.02)" }} />
                  <Legend />
                  <Bar dataKey="assigned" name="Assigned" fill="#dbeafe" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="delivered" name="Delivered" fill="#1890ff" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Col>
        <Col xs={24} lg={8}>
          <div style={{ background: "#fff", padding: 24, borderRadius: 12, border: "1px solid #e8ecf0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", height: "100%" }}>
            <Title level={5} style={{ marginBottom: 20 }}>Delivery Status Ratio</Title>
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: "Delivered", value: overallStats.totalDelivered },
                      { name: "Pending", value: overallStats.totalAssigned - overallStats.totalDelivered }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    <Cell fill="#52c41a" />
                    <Cell fill="#fadb14" />
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ marginTop: 20, textAlign: "center" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Distribution of assigned orders across all personnel</Text>
            </div>
          </div>
        </Col>
      </Row>

      {/* Detailed Table */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Detailed Performance Metrics</span>
          <Input 
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} 
            placeholder="Search by name..." 
            style={{ width: 250, borderRadius: 6 }} 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Table
          dataSource={filteredData}
          columns={[
            { title: "S.No", key: "index", width: 70, align: "center", render: (_, __, i) => i + 1 },
            { title: "Name", dataIndex: "name", key: "name", render: (n) => <span style={{ fontWeight: 600 }}>{n}</span> },
            { title: "Total Assigned", dataIndex: "totalAssigned", key: "assigned", align: "center", sorter: (a, b) => a.totalAssigned - b.totalAssigned },
            { title: "Total Delivered", dataIndex: "totalDelivered", key: "delivered", align: "center", sorter: (a, b) => a.totalDelivered - b.totalDelivered },
            { 
              title: "Success Rate", 
              key: "rate", 
              align: "center",
              render: (_, record) => {
                const rate = record.totalAssigned ? (record.totalDelivered / record.totalAssigned * 100) : 0;
                let color = "red";
                if (rate >= 90) color = "green";
                else if (rate >= 70) color = "blue";
                else if (rate >= 40) color = "orange";
                return <Tag color={color} style={{ borderRadius: 10, fontWeight: 700 }}>{rate.toFixed(1)}%</Tag>
              }
            }
          ]}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "30", "40"] }}
          size="middle"
          rowKey="_id"
        />
      </div>

      <style jsx="true">{`
        .report-card {
          border-radius: 12px;
          transition: all 0.3s;
          border: 1px solid #e8ecf0;
        }
        .report-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
        }
        .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #64748b !important;
          font-weight: 700 !important;
        }
      `}</style>
    </div>
  );
};

export default DeliveryBoyReports;
